import { useEffect, useRef, useState, useCallback, type PointerEvent as ReactPointerEvent } from "react";
import { Button } from "@/components/ui/button";
import { X, Check } from "lucide-react";

export interface FilterPreset {
  label: string;
  filter: string; // CSS filter string applied to canvas context
}

const FILTERS: FilterPreset[] = [
  { label: "Original", filter: "none" },
  { label: "B&W", filter: "grayscale(100%)" },
  { label: "Sepia", filter: "sepia(80%)" },
  { label: "Warm", filter: "saturate(130%) hue-rotate(-10deg) brightness(1.05)" },
  { label: "Cool", filter: "saturate(110%) hue-rotate(180deg) brightness(1.05)" },
  { label: "Vintage", filter: "sepia(40%) contrast(110%) brightness(0.9)" },
  { label: "Vivid", filter: "contrast(150%) saturate(120%)" },
  { label: "Fade", filter: "brightness(1.1) saturate(70%) contrast(0.9)" },
];

const OUTPUT_SIZE = 400;
const MAX_JPEG_BYTES = 500_000; // 500 KB guard

interface ImageEditorProps {
  sourceCanvas: HTMLCanvasElement;
  onConfirm: (bytes: Uint8Array) => void;
  onCancel: () => void;
}

/** Draw the sourceCanvas with a filter into a target canvas of given size */
function renderToCanvas(
  src: HTMLCanvasElement,
  target: HTMLCanvasElement,
  filter: string,
  crop: { x: number; y: number; size: number },
) {
  const ctx = target.getContext("2d")!;
  ctx.clearRect(0, 0, target.width, target.height);
  if (filter !== "none") {
    ctx.filter = filter;
  } else {
    ctx.filter = "none";
  }
  ctx.drawImage(src, crop.x, crop.y, crop.size, crop.size, 0, 0, target.width, target.height);
}

export function ImageEditor({ sourceCanvas, onConfirm, onCancel }: ImageEditorProps) {
  const previewRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const MAX_JPEQ_QUALITY = 0.85;

  const srcW = sourceCanvas.width;
  const srcH = sourceCanvas.height;
  const srcMin = Math.min(srcW, srcH);
  const minCrop = Math.max(100, Math.floor(srcMin * 0.1));

  // Crop: a square region in source image coordinates
  const [crop, setCrop] = useState({
    x: Math.floor((srcW - srcMin) / 2),
    y: Math.floor((srcH - srcMin) / 2),
    size: srcMin,
  });
  const cropRef = useRef(crop);
  cropRef.current = crop;

  const [filterIndex, setFilterIndex] = useState(0);
  const [sending, setSending] = useState(false);

  // Multi-pointer gesture tracking (drag + pinch-to-zoom)
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureRef = useRef<{
    cropX: number; cropY: number; cropSize: number;
    initialDist?: number;
    startX?: number; startY?: number;
  } | null>(null);

  const clampCrop = useCallback((c: { x: number; y: number; size: number }) => {
    const size = Math.max(minCrop, Math.min(srcMin, c.size));
    const x = Math.max(0, Math.min(srcW - size, c.x));
    const y = Math.max(0, Math.min(srcH - size, c.y));
    return { x, y, size };
  }, [srcW, srcH, srcMin, minCrop]);

  // --- Render preview whenever crop/filter changes ---
  useEffect(() => {
    const canvas = previewRef.current;
    if (!canvas) return;
    renderToCanvas(sourceCanvas, canvas, FILTERS[filterIndex]!.filter, crop);
  }, [crop, filterIndex, sourceCanvas]);

  // --- Render filter thumbnails once on mount ---
  useEffect(() => {
    const thumbCrop = { x: crop.x, y: crop.y, size: crop.size };
    FILTERS.forEach((f, i) => {
      const thumb = thumbnailRefs.current[i];
      if (!thumb) return;
      renderToCanvas(sourceCanvas, thumb, f.filter, thumbCrop);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceCanvas]); // intentionally once per mount; thumbs don't track crop position

  // --- Gesture handlers: single-finger drag + two-finger pinch-to-zoom ---
  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const cur = cropRef.current;
    if (pointersRef.current.size === 1) {
      // Single finger — start drag
      gestureRef.current = {
        cropX: cur.x, cropY: cur.y, cropSize: cur.size,
        startX: e.clientX, startY: e.clientY,
      };
    } else if (pointersRef.current.size === 2) {
      // Second finger — switch to pinch
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[1]!.x - pts[0]!.x, pts[1]!.y - pts[0]!.y);
      gestureRef.current = {
        cropX: cur.x, cropY: cur.y, cropSize: cur.size,
        initialDist: dist,
      };
    }
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = gestureRef.current;
    if (!g) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const el = containerRef.current;
    if (!el) return;

    if (pointersRef.current.size === 1 && g.startX != null && g.startY != null) {
      // Single-finger pan — inverted so dragging moves the image naturally
      const containerSize = el.getBoundingClientRect().width;
      const scale = g.cropSize / containerSize;
      const dx = Math.round((e.clientX - g.startX) * scale);
      const dy = Math.round((e.clientY - g.startY) * scale);
      setCrop(clampCrop({ x: g.cropX - dx, y: g.cropY - dy, size: g.cropSize }));
    } else if (pointersRef.current.size === 2 && g.initialDist) {
      // Pinch-to-zoom: fingers spread → zoom in (smaller crop), pinch → zoom out
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[1]!.x - pts[0]!.x, pts[1]!.y - pts[0]!.y);
      const ratio = g.initialDist / dist;
      const newSize = Math.round(g.cropSize * ratio);
      const sizeDiff = newSize - g.cropSize;
      setCrop(clampCrop({
        x: g.cropX - Math.round(sizeDiff / 2),
        y: g.cropY - Math.round(sizeDiff / 2),
        size: newSize,
      }));
    }
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size === 0) {
      gestureRef.current = null;
    } else if (pointersRef.current.size === 1) {
      // Lifted one finger during pinch — transition back to drag
      const [pt] = [...pointersRef.current.values()];
      const cur = cropRef.current;
      gestureRef.current = {
        cropX: cur.x, cropY: cur.y, cropSize: cur.size,
        startX: pt!.x, startY: pt!.y,
      };
    }
  };

  // --- Export at 400×400 and call onConfirm ---
  const handleSend = async () => {
    setSending(true);
    try {
      const out = document.createElement("canvas");
      out.width = OUTPUT_SIZE;
      out.height = OUTPUT_SIZE;
      renderToCanvas(sourceCanvas, out, FILTERS[filterIndex]!.filter, crop);

      let quality = MAX_JPEQ_QUALITY;
      let blob: Blob | null = await new Promise((res) =>
        out.toBlob(res, "image/jpeg", quality),
      );

      // Size guard: if somehow over 500KB, reduce quality once
      if (blob && blob.size > MAX_JPEG_BYTES) {
        quality = 0.7;
        blob = await new Promise((res) => out.toBlob(res, "image/jpeg", quality));
      }

      if (!blob) throw new Error("Failed to encode image");

      const buf = await blob.arrayBuffer();
      onConfirm(new Uint8Array(buf));
    } catch {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 active:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        <span className="text-white/70 text-sm font-medium">Edit photo</span>
        <Button
          size="sm"
          onClick={handleSend}
          disabled={sending}
          className="bg-app-primary hover:bg-app-primary-hover rounded-full px-4 h-8 text-xs font-semibold"
        >
          {sending ? (
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 border border-white/60 border-t-transparent rounded-full animate-spin" />
              Encrypting…
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" />
              Send
            </span>
          )}
        </Button>
      </div>

      {/* Preview canvas — fills available space as a square */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden px-2"
      >
        <div className="relative" style={{ width: "min(100%, calc(100vh - 200px))", aspectRatio: "1" }}>
          <canvas
            ref={previewRef}
            width={OUTPUT_SIZE}
            height={OUTPUT_SIZE}
            className="w-full h-full rounded-xl"
          />
          {/* Touch / pointer gesture overlay */}
          <div
            className="absolute inset-0 cursor-grab active:cursor-grabbing rounded-xl"
            style={{ touchAction: "none" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />
          {/* Gesture hint */}
          <div className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none">
            <span className="text-[11px] text-white/50 bg-black/30 px-2 py-0.5 rounded-full">
              Drag to reframe · Pinch to zoom
            </span>
          </div>
        </div>
      </div>

      {/* Filter strip */}
      <div className="shrink-0 pb-safe pb-6 pt-3">
        <div className="flex gap-3 overflow-x-auto px-4 scrollbar-none pb-1">
          {FILTERS.map((f, i) => (
            <button
              key={f.label}
              type="button"
              onClick={() => setFilterIndex(i)}
              className={`flex flex-col items-center gap-1 shrink-0 transition-transform active:scale-95 ${
                i === filterIndex ? "scale-105" : ""
              }`}
            >
              <div
                className={`w-14 h-14 rounded-xl overflow-hidden ring-2 transition-all ${
                  i === filterIndex ? "ring-app-primary shadow-lg shadow-app-primary/30" : "ring-transparent"
                }`}
              >
                <canvas
                  ref={(el) => { thumbnailRefs.current[i] = el; }}
                  width={56}
                  height={56}
                  className="w-full h-full"
                />
              </div>
              <span
                className={`text-[10px] font-medium ${
                  i === filterIndex ? "text-app-primary" : "text-white/50"
                }`}
              >
                {f.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
